import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Chip
} from '@mui/material';
import { collection, query, getDocs, getDoc, doc } from 'firebase/firestore';
import { db, functions } from '../../../../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { SUBSCRIPTION_TYPES } from './constants';
import { FaUserShield, FaChalkboardTeacher } from 'react-icons/fa';
import './dialogs.css';

const AssignMentorDialog = ({ 
  open, 
  onClose, 
  onSubmit, 
  selectedUser,
  selectedSubscriptionType
}) => {
  // # State for mentors list and selection
  const [mentors, setMentors] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const subscriptionInfo = SUBSCRIPTION_TYPES[selectedSubscriptionType.toUpperCase()];
  const currentSubscription = selectedUser?.subscriptions[selectedSubscriptionType];

  // # Fetch available mentors (both admins and tutors)
  useEffect(() => {
    const fetchMentors = async () => {
      try {
        setIsLoading(true);
        // # Get the administration document
        const emailsDoc = await getDoc(doc(db, 'administration', 'emails'));
        const emailsData = emailsDoc.exists() ? emailsDoc.data() : {};
        
        // # Get admin and tutor emails
        const adminEmails = emailsData.admins || [];
        const tutorEmails = emailsData.tutors || [];
        
        // # Get all users
        const usersQuery = query(collection(db, 'users'));
        const usersSnapshot = await getDocs(usersQuery);
        
        // # Create combined mentors list with role information
        const mentorsList = [];
        
        // # Process admin users
        usersSnapshot.docs
          .filter(doc => adminEmails.includes(doc.data().email))
          .forEach(doc => {
            mentorsList.push({
              uid: doc.id,
              email: doc.data().email,
              name: `${doc.data().firstName} ${doc.data().lastName}`.trim(),
              firstName: doc.data().firstName || '',
              lastName: doc.data().lastName || '',
              role: 'admin'
            });
          });
        
        // # Process tutor users
        usersSnapshot.docs
          .filter(doc => tutorEmails.includes(doc.data().email))
          .forEach(doc => {
            mentorsList.push({
              uid: doc.id,
              email: doc.data().email,
              name: `${doc.data().firstName} ${doc.data().lastName}`.trim(),
              firstName: doc.data().firstName || '',
              lastName: doc.data().lastName || '',
              role: 'tutor'
            });
          });
        
        setMentors(mentorsList);
      } catch (error) {
        console.error('Error fetching mentors:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      fetchMentors();
    }
  }, [open]);

  // # Handle mentor selection
  const handleMentorChange = (event) => {
    setSelectedMentor(event.target.value);
  };

  // # Handle submit with complete mentor information for batch write
  const handleSubmit = () => {
    const selectedMentorData = mentors.find(mentor => mentor.email === selectedMentor);
    if (selectedMentorData) {
      // # Prepare data for batch write in subscription_management.js
      const updateData = {
        ...currentSubscription,
        // # Explicitly preserve subscription status and dates
        status: currentSubscription.status,
        startDate: currentSubscription.startDate,
        endDate: currentSubscription.endDate,
        active: currentSubscription.active,
        // # Update only mentor information
        mentor: {
          ...selectedMentorData,
          displayRole: selectedMentorData.role === 'admin' ? 'Admin (Mentor)' : 'Tutor'
        }
      };
      
      // # Log the mentor assignment for tracking
      const logSubscription = httpsCallable(functions, 'logSubscription');
      logSubscription({
        uid: selectedUser.uid,
        subscriptionType: selectedSubscriptionType,
        action: 'assign_mentor',
        newMentor: selectedMentorData.uid
      })
      .then((result) => {
        console.log('Mentor assignment logged successfully:', result.data);
        
        // # Create notification for the mentor assignment
        const createNotification = httpsCallable(functions, 'createSubscriptionNotification');
        createNotification({
          uid: selectedUser.uid,
          subscriptionType: selectedSubscriptionType,
          startDate: currentSubscription.startDate,
          endDate: currentSubscription.endDate,
          action: 'assign_mentor'
        })
        .then(notificationResult => {
          console.log('Mentor assignment notification sent:', notificationResult.data);
        })
        .catch(error => {
          console.error('Error sending mentor assignment notification:', error);
        });
      })
      .catch((error) => {
        console.error('Error logging mentor assignment:', error);
      });
      
      // # Pass the update data to parent component for batch processing
      // # The mentor document will be updated in the Cloud Function when the batch is processed
      onSubmit(updateData);
    }
  };

  // # Get current mentor display information
  const getCurrentMentorDisplay = () => {
    if (!currentSubscription?.mentor) return 'None assigned';
    if (typeof currentSubscription.mentor === 'string') return currentSubscription.mentor;
    return `${currentSubscription.mentor.name} (${currentSubscription.mentor.email}) - ${currentSubscription.mentor.displayRole || currentSubscription.mentor.role || 'Unknown Role'}`;
  };

  // # Render role chip
  const RoleChip = ({ role }) => (
    <Chip
      icon={role === 'admin' ? <FaUserShield size={12} /> : <FaChalkboardTeacher size={12} />}
      label={role === 'admin' ? 'Admin (Mentor)' : 'Tutor'}
      size="small"
      color={role === 'admin' ? 'primary' : 'secondary'}
      sx={{ ml: 1 }}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="dialog-title">
        Assign Mentor for {subscriptionInfo.name}
        {selectedUser && (
          <Typography variant="subtitle2" className="dialog-subtitle">
            for {selectedUser.userName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent className="dialog-content">
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Current mentor: {getCurrentMentorDisplay()}
        </Typography>

        {isLoading ? (
          <div className="loading-container">
            <CircularProgress size={24} />
            <Typography>Loading available mentors...</Typography>
          </div>
        ) : (
          <FormControl fullWidth>
            <InputLabel>Select Mentor</InputLabel>
            <Select
              value={selectedMentor}
              onChange={handleMentorChange}
              label="Select Mentor"
            >
              {mentors.map((mentor) => (
                <MenuItem 
                  key={mentor.uid} 
                  value={mentor.email}
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    {mentor.name} ({mentor.email})
                    <RoleChip role={mentor.role} />
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Typography className="info-text" sx={{ mt: 2 }}>
          Note: Only administrators and tutors can be assigned as mentors.
        </Typography>
      </DialogContent>
      <DialogActions className="dialog-actions">
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained" 
          color="primary"
          disabled={!selectedMentor || isLoading}
        >
          Assign Mentor
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignMentorDialog; 